/**
 * Enhanced Search with Autocomplete
 * Amazon-style search with dropdown suggestions
 * With Mode-based filtering (Shop/Eats)
 */

(function() {
  'use strict';
  
  const input = document.getElementById('search-input');
  const form = document.getElementById('search-form');
  const box = document.getElementById('search-suggest');
  
  if (!input || !box) return;

  let controller;
  let selectedIndex = -1;
  let suggestions = [];
  
  const MODE_KEY = 'veda_mode_preference';

  // Get current mode
  function getCurrentMode() {
    try {
      return localStorage.getItem(MODE_KEY) || 'shop';
    } catch(e) {
      return 'shop';
    }
  }

  // Filter products by mode using tags (mode:shop or mode:eats)
  function filterByMode(products, mode) {
    return products.filter(function(product) {
      var tags = (product.tags || []).map(function(t) { return t.toLowerCase(); });
      var hasShopTag = tags.some(function(t) { return t === 'mode:shop'; });
      var hasEatsTag = tags.some(function(t) { return t === 'mode:eats'; });
      
      if (mode === 'eats') {
        // Show only eats products
        return hasEatsTag;
      } else if (mode === 'shop') {
        // Show only shop products (or products without mode tag = default to shop)
        return hasShopTag || (!hasShopTag && !hasEatsTag);
      }
      
      return true;
    });
  }

  // Debounce function
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Format price
  function formatPrice(priceValue) {
    // Search API returns price in correct format (string or number like "19.95" or 19.95)
    // NOT in cents, so just parse and format it
    const price = parseFloat(priceValue);
    return `£${price.toFixed(2)}`;
  }

  // Highlight matching text
  function highlightMatch(text, query) {
    if (!query) return text;
    const regex = new RegExp(`(${query})`, 'gi');
    return text.replace(regex, '<strong>$1</strong>');
  }

  // Fetch suggestions
  async function fetchSuggestions(query) {
    if (!query) {
      box.hidden = true;
      box.innerHTML = '';
      return;
    }

    controller?.abort?.();
    controller = new AbortController();

    try {
      // Show loading state
      box.innerHTML = '<div class="search-loading">Searching...</div>';
      box.hidden = false;

      const res = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=12&resources[options][unavailable_products]=hide`,
        { signal: controller.signal }
      );
      
      const data = await res.json();
      let products = data.resources?.results?.products || [];
      
      // Filter products by current mode
      const currentMode = getCurrentMode();
      products = filterByMode(products, currentMode);
      
      suggestions = products;

      if (products.length === 0) {
        const modeLabel = currentMode === 'eats' ? 'Eats' : 'Shop';
        box.innerHTML = `
          <div class="search-no-results">
            <p>No ${modeLabel} results found for "${query}"</p>
            <p class="search-suggestion">Try different keywords or switch mode</p>
          </div>
        `;
        box.hidden = false;
        return;
      }

      let html = '';

      // Products section
      if (products.length > 0) {
        html += '<div class="search-section">';
        html += '<div class="search-section-title">Products</div>';
        html += products.slice(0, 8).map((p, idx) => `
          <a class="search-item ${idx === selectedIndex ? 'selected' : ''}" 
             href="${p.url}" 
             data-index="${idx}">
            <div class="search-item-image">
              ${p.image ? `<img src="${p.image}" alt="${p.title}" loading="lazy">` : '<div class="no-image">No Image</div>'}
            </div>
            <div class="search-item-content">
              <div class="search-item-title">${highlightMatch(p.title, query)}</div>
              <div class="search-item-price">${formatPrice(p.price)}</div>
              ${p.vendor ? `<div class="search-item-vendor">${p.vendor}</div>` : ''}
            </div>
          </a>
        `).join('');
        html += '</div>';
      }

      // View all results button
      html += `
        <div class="search-footer">
          <button type="submit" class="search-view-all">
            View all results for "${query}"
            <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"/>
            </svg>
          </button>
        </div>
      `;

      box.innerHTML = html;
      box.hidden = false;

    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error('Search error:', err);
        box.hidden = true;
      }
    }
  }

  // Debounced search
  const debouncedSearch = debounce(fetchSuggestions, 300);

  // Input event
  input.addEventListener('input', (e) => {
    const query = e.target.value.trim();
    selectedIndex = -1;
    debouncedSearch(query);
  });

  // Keyboard navigation
  input.addEventListener('keydown', (e) => {
    const items = box.querySelectorAll('.search-item:not(.search-item-collection)');
    
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      selectedIndex = Math.min(selectedIndex + 1, items.length - 1);
      updateSelection(items);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      selectedIndex = Math.max(selectedIndex - 1, -1);
      updateSelection(items);
    } else if (e.key === 'Enter') {
      if (selectedIndex >= 0 && items[selectedIndex]) {
        e.preventDefault();
        items[selectedIndex].click();
      }
      // Otherwise let form submit naturally
    } else if (e.key === 'Escape') {
      box.hidden = true;
      selectedIndex = -1;
    }
  });

  // Update selection styling
  function updateSelection(items) {
    items.forEach((item, idx) => {
      if (idx === selectedIndex) {
        item.classList.add('selected');
        item.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      } else {
        item.classList.remove('selected');
      }
    });
  }

  // Click outside to close
  document.addEventListener('click', (e) => {
    if (!box.contains(e.target) && e.target !== input) {
      box.hidden = true;
      selectedIndex = -1;
    }
  });

  // Form submit - go to search results page
  if (form) {
    form.addEventListener('submit', (e) => {
      const query = input.value.trim();
      if (!query) {
        e.preventDefault();
        return;
      }
      // Let form submit naturally to /search?q=...
      box.hidden = true;
    });
  }

  // Focus input on key press (optional - like Amazon)
  document.addEventListener('keydown', (e) => {
    // Don't trigger if typing in another input
    if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
    
    // Focus search on any letter/number key
    if (e.key.length === 1 && /[a-z0-9]/i.test(e.key)) {
      input.focus();
    }
  });

  // Listen for mode changes to refresh suggestions if search is open
  document.addEventListener('veda:mode:changed', () => {
    if (input.value.trim().length >= 1 && !box.hidden) {
      fetchSuggestions(input.value.trim());
    }
  });

})();
